"use client";

import { useMemo } from "react";
import { toast_configs } from "@core/constants/toast_configs";
import useWidgetFormRenderer from "@core/helpers/client/hooks/useWidgetFormRenderer";
import Button from "@/components/ui/button";
import { SITE_FIELD_COMPONENTS } from "./siteFields";
import { CUSTOM_FIELDS } from "./customFields";
import { CUSTOM_RULES, SITE_MESSAGES, merge_messages } from "./siteChecks";
import { CORE_PART_CLASS_NAMES, GRID_CLASS_NAMES } from "./formClassNames";

// فرمی از فرم‌ساز که مدیر روی یک ویجت گذاشته (تماس با ما، همکاری با ما). فیلدها، شرط‌ها، بررسی‌ها و ثبت پاسخ با core است؛
// این‌جا فقط ظاهر است: اینپوت‌های ژومنت برای همه‌ی نوع‌ها، فیلدهای سفارشی قالب با قانون‌هایشان، دکمه‌ی ارسال و toastها.
// مدیر هر فیلدی را در هر فرمی می‌تواند بگذارد، پس هیچ چیز این‌جا فرض فرم خاصی ندارد. بعد از ثبت موفق فرم خالی می‌شود.
// widget باید form_definition_data داشته باشد (with_widget_forms سمت سرور اضافه‌اش می‌کند)؛ بدون آن چیزی کشیده نمی‌شود.

const TOAST_OPTIONS = { className: "custome_toastify", position: "bottom-center", autoClose: 4000 };

// جای ماژول CSS پیش‌فرض core (class_names): قاب فرم و گروه (بدون کادر) و هر بخشی که خود core می‌کشد
export const WIDGET_FORM_CLASS_NAMES = {
	...CORE_PART_CLASS_NAMES,
	...GRID_CLASS_NAMES,
	form_root: "flex flex-col",
	group_root: "flex flex-col",
	group_title: "text-base font-bold leading-7 text-jmt-main mb-3",
	group_description: "text-sm text-jmt-neutral-500 mb-3",
	group_grid: "grid grid-cols-12 items-baseline gap-x-4",
};

// field_components: نوع‌هایی که این صفحه شکل دیگری می‌خواهد (روی اینپوت‌های مشترک گذاشته می‌شود).
// messages: متن‌های خطای مخصوص این فرم ({ fa: { errors: { <code>: "..." } } }) روی متن‌های مشترک. هر دو ثابت ماژول باشند.
const WidgetForm = ({ widget, field_components, messages }) => {
	const components = useMemo(() => ({ ...SITE_FIELD_COMPONENTS, ...field_components }), [field_components]);
	const texts = useMemo(() => merge_messages(SITE_MESSAGES, messages), [messages]);

	const form = useWidgetFormRenderer(widget, {
		field_components: components,
		custom_fields: CUSTOM_FIELDS,
		custom_rules: CUSTOM_RULES,
		messages: texts,
		class_names: WIDGET_FORM_CLASS_NAMES,
		stable_class_names: true,
		scroll_to_error: true,
		// فایل‌ها هنگام ثبت فرم، جدا و با کپچا به سرور می‌روند (نه base64 داخل پاسخ)
		upload_files: true,
	});

	const group_keys = useMemo(() => (form.definition?.version?.groups || []).map((group) => group?.key), [form.definition]);

	if (!form.definition) return null;

	const send = async (event) => {
		event.preventDefault();
		const result = await form.submit();
		if (result?.status === "success") {
			toast_configs(" درخواست شما با موفقیت ثبت شد ", "success", TOAST_OPTIONS);
			form.reset();
		} else if (result?.status === "invalid") {
			toast_configs(" لطفا مقادیر را به درستی وارد کنید ", "error", TOAST_OPTIONS);
		}
	};

	return (
		<form noValidate onSubmit={send} className="flex flex-col lg:gap-2">
			{form.render_groups(group_keys)}
			{form.captcha_node}
			{form.form_error && <p role="alert" className="text-xs text-jmt-red-600">{form.form_error}</p>}
			<Button
				type="submit"
				color_type="primary"
				text={form.submitting ? "درحال ارسال" : "ارسال پیام"}
				loading={form.submitting}
				className="w-full"
			/>
		</form>
	);
};

export default WidgetForm;
